import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { readUtms, trackConversion } from "@/lib/track";

export default function Contact() {
  const [countries, setCountries] = useState([]);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const fromCalc = params.get("from") === "calculator";
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", company: "", email: "", phone: "",
    country_of_origin: "", target_country: "canada",
    business_type: "", services: "", budget: "", timeline: "", notes: "",
  });

  useEffect(() => { api.get("/countries").then((r) => setCountries(r.data)); }, []);

  useEffect(() => {
    if (fromCalc) {
      const stored = sessionStorage.getItem("gos_quote");
      if (stored) {
        const { country, selections, quote } = JSON.parse(stored);
        const serviceLabels = quote?.lines?.map((l) => l.service_name).join(", ") || "";
        setForm((f) => ({
          ...f, target_country: country, services: serviceLabels,
          notes: `Calculator estimate: ${quote?.currency} ${quote?.grand_total?.toLocaleString()} (12-month)`,
        }));
      }
    }
  }, [fromCalc]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/leads", {
        ...form,
        services: form.services ? form.services.split(",").map((s) => s.trim()).filter(Boolean) : [],
        utm: readUtms() || {},
        referrer: document.referrer || "",
      });
      trackConversion("lead", { value: 25 });
      toast.success("Thanks! Our team will be in touch within one business day.");
      navigate("/");
    } catch (e) {
      toast.error("Submission failed. Please check your details and try again.");
    } finally { setSubmitting(false); }
  };

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="contact-page" className="fade-in py-20">
      <div className="container-gos grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="overline mb-3">Talk to us</div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-gos-charcoal">Book a free consultation.</h1>
          <p className="text-gos-slate mt-4 leading-relaxed">A senior consultant will review your goals, recommend the right structure, and outline the costs and timeline for your target country.</p>
          <ul className="mt-8 space-y-3 text-sm text-gos-slate">
            <li>✓ 30-minute strategy call</li>
            <li>✓ No obligation — we&apos;ll be honest if we&apos;re not the right fit</li>
            <li>✓ A written proposal within 2 business days</li>
          </ul>
        </div>

        <form onSubmit={submit} data-testid="lead-form" className="lg:col-span-7 bg-white border border-gos-border rounded-sm p-8 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Full name" value={form.full_name} onChange={(v) => upd("full_name", v)} required testid="lead-full-name" />
            <Field label="Company" value={form.company} onChange={(v) => upd("company", v)} testid="lead-company" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => upd("email", v)} required testid="lead-email" />
            <Field label="WhatsApp / phone" value={form.phone} onChange={(v) => upd("phone", v)} testid="lead-phone" />
            <Field label="Country of origin" value={form.country_of_origin} onChange={(v) => upd("country_of_origin", v)} testid="lead-origin" />
            <Select label="Target country" value={form.target_country} onChange={(v) => upd("target_country", v)} testid="lead-target">
              {countries.map((c) => <option key={c.slug} value={c.slug}>{c.flag} {c.name}</option>)}
            </Select>
            <Field label="Business type" value={form.business_type} onChange={(v) => upd("business_type", v)} testid="lead-business-type" />
            <Field label="Budget (USD)" value={form.budget} onChange={(v) => upd("budget", v)} testid="lead-budget" />
            <Field label="Timeline" value={form.timeline} onChange={(v) => upd("timeline", v)} testid="lead-timeline" />
            <Field label="Services interested in" value={form.services} onChange={(v) => upd("services", v)} placeholder="Comma separated" testid="lead-services" />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">Notes</label>
            <textarea data-testid="lead-notes" value={form.notes} onChange={(e) => upd("notes", e.target.value)} rows={4}
              className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
          </div>

          <button type="submit" disabled={submitting} className="btn-primary w-full" data-testid="lead-submit-btn">
            {submitting ? "Sending…" : "Send request"}
          </button>
          <p className="text-[10px] text-gos-muted">By submitting, you agree to be contacted about your inquiry. We respect your privacy.</p>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}{required && " *"}</label>
      <input data-testid={testid} type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy" />
    </div>
  );
}
function Select({ label, value, onChange, testid, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gos-muted mb-1">{label}</label>
      <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full border border-gos-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-gos-navy bg-white">
        {children}
      </select>
    </div>
  );
}
